import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createAuthenticatedApiClient } from '../../config/api';

// Define interfaces for note moderation data
interface NoteFlag {
  _id: string;
  reportedBy: string;
  reason: string;
  description: string;
  createdAt: string;
  status: 'pending' | 'resolved';
  resolution?: string;
  resolvedBy?: string;
  resolvedAt?: string;
}

type ModerationStatus = 'pending' | 'ai-approved' | 'moderator-approved' | 'rejected';

/**
 * A moderator reference as GET /moderator/notes/:id returns it: populated to
 * a name when the user still exists, otherwise the raw id.
 */
export type ModeratorRef = string | { _id?: string; firstName?: string; lastName?: string } | null;

/**
 * One moderator edit. Entries saved before previousNote/newNote existed only
 * have `originalNote` (the text before that edit).
 */
export interface ModerationHistoryEntry {
  _id?: string;
  timestamp: string;
  previousNote?: string;
  newNote?: string;
  moderatorId?: ModeratorRef;
  originalNote?: string;
}

/** One approve/reject decision from the moderation.decisions log. */
export interface ModerationDecisionEntry {
  _id?: string;
  status: ModerationStatus;
  comment?: string;
  moderatorId?: ModeratorRef;
  timestamp: string;
}

/** "moderator Jane Doe", "moderator <id>" for an unpopulated id, or a fallback. */
export const describeModerator = (ref: ModeratorRef | undefined): string => {
  if (!ref) return 'a moderator (not recorded)';
  if (typeof ref === 'string') return `moderator ${ref}`;
  const name = [ref.firstName, ref.lastName].filter(Boolean).join(' ').trim();
  if (name) return `moderator ${name}`;
  return ref._id ? `moderator ${ref._id}` : 'a moderator (not recorded)';
};

interface ModerationNoteDetail {
  _id: string;
  note: string;
  /** The text exactly as the contributor submitted it (moderators only). */
  originalNote?: string;
  /** Legacy shape; GET /moderator/notes/:id returns the raw document, so use moderation.status. */
  status?: ModerationStatus;
  isFlagged: boolean;
  flagCount: number;
  createdAt: string;
  updatedAt: string;
  qualityScore?: number;
  contributorInfo: {
    userId: string;
    firstName: string;
    lastName: string;
    aliasName: string;
    email: string;
  };
  location: {
    city: string;
    state: string;
    zipcode: string;
  };
  moderation?: {
    status?: ModerationStatus;
    aiDecision?: string;
    ai_review_notes?: string;
    moderatorComment?: string;
    moderatorId?: ModeratorRef;
    moderatedAt?: string;
    qualityScore?: number;
    edited?: boolean;
    history?: ModerationHistoryEntry[];
    decisions?: ModerationDecisionEntry[];
  };
  flags: Array<{
    _id: string;
    reason: string;
    description: string;
    createdAt: string;
    resolved: boolean;
    status?: string;
    reportedBy?: string;
    resolution?: string;
    resolvedBy?: string;
    resolvedAt?: string;
  }>;
}

interface ModerationNoteResponse {
  success: boolean;
  data: ModerationNoteDetail;
}

/** The text as submitted: originalNote, else the text before the first recorded edit. */
export const getOriginalText = (note: Pick<ModerationNoteDetail, 'originalNote' | 'moderation'>): string | undefined => {
  if (note.originalNote) return note.originalNote;
  const first = note.moderation?.history?.[0];
  return first?.previousNote ?? first?.originalNote;
};

/**
 * True when a moderator has changed the text. Based on the moderator edit
 * record only (the edited flag, or a legacy history entry): an owner's own
 * rewrite also makes the text differ from originalNote, but that isn't a
 * moderator edit.
 */
export const isModeratorEdited = (note: Pick<ModerationNoteDetail, 'moderation'>): boolean =>
  Boolean(note.moderation?.edited) ||
  (note.moderation?.history?.length ?? 0) > 0;

const NoteModerationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // State management
  const [note, setNote] = useState<ModerationNoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [editedContent, setEditedContent] = useState('');
  const [moderatorComment, setModeratorComment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'moderator-approved' | 'rejected'>('moderator-approved');
  const [isEditingContent, setIsEditingContent] = useState(false);

  // Fetch note details for moderation
  const fetchNoteDetails = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);

      const apiClient = createAuthenticatedApiClient();
      const response = await apiClient.get<ModerationNoteResponse>(`/moderator/notes/${id}`);

      if (response.data.success) {
        setNote(response.data.data);
        setEditedContent(response.data.data.note);
        // Start each decision with an empty comment box. Prefilling the last
        // comment re-sent it (e.g. an old reject reason) with the next decision;
        // it is still shown below as "Previous Comment".
        setModeratorComment('');
      } else {
        throw new Error('Failed to load note');
      }
    } catch (err) {
      console.error('Error fetching note:', err);
      setError(err instanceof Error ? err.message : 'Failed to load note');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Update note moderation status
  const updateModerationStatus = async () => {
    if (!id || !note) return;
    
    try {
      setSaving(true);
      
      const apiClient = createAuthenticatedApiClient();
      await apiClient.put(`/moderator/notes/${id}/status`, {
        status: selectedStatus,
        moderatorComment: moderatorComment.trim() || undefined
      });

      // Refresh note data and navigate back to queue
      await fetchNoteDetails();
      
      // Show success message and redirect
      alert(`Note successfully ${selectedStatus === 'moderator-approved' ? 'approved' : 'rejected'}`);
      navigate('/moderator/queue');
    } catch (err) {
      console.error('Error updating note status:', err);
      alert(err instanceof Error ? err.message : 'Failed to update note status');
    } finally {
      setSaving(false);
    }
  };

  // Update note content
  const updateNoteContent = async () => {
    if (!id || !note || editedContent.trim() === note.note) return;
    
    try {
      setSaving(true);
      
      const apiClient = createAuthenticatedApiClient();
      await apiClient.put(`/moderator/notes/${id}/content`, {
        note: editedContent.trim()
      });

      // Refresh note data
      await fetchNoteDetails();
      setIsEditingContent(false);
      
    } catch (err) {
      console.error('Error updating note content:', err);
      const serverMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      alert(serverMessage || (err instanceof Error ? err.message : 'Failed to update note content'));
    } finally {
      setSaving(false);
    }
  };

  // Resolve a specific flag
  const resolveFlag = async (flagId: string, resolution: string) => {
    if (!id || !note) return;
    
    try {
      setSaving(true);
      
      const apiClient = createAuthenticatedApiClient();
      await apiClient.put(`/moderator/notes/${id}/flags/${flagId}/resolve`, {
        resolution: resolution,
        moderatorComment: `Flag resolved: ${resolution}`
      });

      // Refresh note data
      await fetchNoteDetails();
      
      alert('Flag resolved successfully');
    } catch (err) {
      console.error('Error resolving flag:', err);
      alert(err instanceof Error ? err.message : 'Failed to resolve flag');
    } finally {
      setSaving(false);
    }
  };

  // Effect to fetch note details on mount and when ID changes
  useEffect(() => {
    if (id) {
      fetchNoteDetails();
    }
  }, [id, fetchNoteDetails]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    let badgeClass = 'badge ';
    
    switch (status) {
      case 'pending':
        badgeClass += 'bg-warning text-dark';
        break;
      case 'ai-approved':
        badgeClass += 'bg-info text-white';
        break;
      case 'moderator-approved':
        badgeClass += 'bg-success';
        break;
      case 'rejected':
        badgeClass += 'bg-danger';
        break;
      default:
        badgeClass += 'bg-secondary';
    }
    
    return <span className={badgeClass} data-testid="moderation-status-badge">{status}</span>;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading note details...</p>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5>Error Loading Note</h5>
        <p>{error || 'Note not found'}</p>
        <button className="btn btn-primary" onClick={() => navigate('/moderator/queue')}>
          Back to Queue
        </button>
      </div>
    );
  }

  const edited = isModeratorEdited(note);
  const originalText = getOriginalText(note);
  const history = note.moderation?.history ?? [];
  const decisions = note.moderation?.decisions ?? [];
  const originalDiffers = originalText !== undefined && originalText !== note.note;

  return (
    <div className="note-moderation-view">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Note Moderation</h2>
          <div className="d-flex gap-2 align-items-center">
            {getStatusBadge(note.moderation?.status ?? note.status ?? 'unknown')}
            {note.isFlagged && <span className="badge bg-danger">Flagged</span>}
            {note.qualityScore !== undefined && (
              <span className={`badge ${note.qualityScore >= 70 ? 'bg-success' : note.qualityScore >= 40 ? 'bg-warning text-dark' : 'bg-danger'}`}>
                Quality: {note.qualityScore}
              </span>
            )}
          </div>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/moderator/queue')}>
          <i className="fas fa-arrow-left me-2"></i>
          Back to Queue
        </button>
      </div>

      <div className="row">
        {/* Main Content Column */}
        <div className="col-lg-8">
          {/* Note Content Section */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Note Content</h5>
              {!isEditingContent && (
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setIsEditingContent(true)}
                >
                  <i className="fas fa-edit me-1"></i>
                  Edit Content
                </button>
              )}
            </div>
            <div className="card-body">
              {isEditingContent ? (
                <div>
                  <textarea
                    className="form-control mb-3"
                    rows={6}
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    placeholder="Note content..."
                  />
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-success btn-sm"
                      onClick={updateNoteContent}
                      disabled={saving || editedContent.trim() === note.note}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setEditedContent(note.note);
                        setIsEditingContent(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="note-content-display">
                  <p className="lead" data-testid="current-note-text">{note.note}</p>
                  {edited && (
                    <div className="alert alert-info" data-testid="edited-notice">
                      <small>
                        <i className="fas fa-info-circle me-1"></i>
                        Content has been edited by a moderator. The public only sees the text above.
                      </small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Original, edit history and decisions (moderators only; never shown to the public or the author) */}
          {(edited || originalDiffers || decisions.length > 0) && (
            <div className="card mb-4" data-testid="edit-record">
              <div className="card-header">
                <h5 className="mb-0">Edit Record</h5>
              </div>
              <div className="card-body">
                {originalText !== undefined && (edited || originalDiffers) && (
                  <details className="mb-3" data-testid="original-note-panel">
                    <summary>Original as submitted</summary>
                    <p className="mt-2 mb-0 text-muted" style={{ whiteSpace: 'pre-wrap' }} data-testid="original-note-text">
                      {originalText}
                    </p>
                  </details>
                )}
                {history.length > 0 && (
                  <div data-testid="edit-history">
                    <strong>Edit history</strong>
                    <ol className="list-unstyled mb-0 mt-2">
                      {history.map((entry, i) => (
                        <li key={entry._id ?? i} className="border-top pt-2 mt-2" data-testid="edit-history-entry">
                          <small className="text-muted">
                            {formatDate(entry.timestamp)}
                            {' by '}
                            {describeModerator(entry.moderatorId)}
                          </small>
                          <div className="small mt-1">
                            <span className="text-decoration-line-through text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                              {entry.previousNote ?? entry.originalNote ?? '(not recorded)'}
                            </span>
                            {' \u2192 '}
                            <span style={{ whiteSpace: 'pre-wrap' }}>{entry.newNote ?? '(not recorded)'}</span>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {decisions.length > 0 && (
                  <div className={history.length > 0 ? 'mt-3' : undefined} data-testid="decision-history">
                    <strong>Decisions</strong>
                    <ol className="list-unstyled mb-0 mt-2">
                      {decisions.map((d, i) => (
                        <li key={d._id ?? i} className="border-top pt-2 mt-2" data-testid="decision-entry">
                          <small className="text-muted">
                            {formatDate(d.timestamp)}
                            {' by '}
                            {describeModerator(d.moderatorId)}
                          </small>
                          <div className="small mt-1">
                            <strong>{d.status === 'moderator-approved' ? 'Approved' : d.status === 'rejected' ? 'Rejected' : d.status}</strong>
                            {d.comment ? (
                              <span style={{ whiteSpace: 'pre-wrap' }}>{`: ${d.comment}`}</span>
                            ) : (
                              <span className="text-muted"> (no comment)</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Decision Section */}
          {note.moderation?.aiDecision && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">AI Analysis</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <strong>AI Decision:</strong>
                    <p className="mb-2">{note.moderation.aiDecision}</p>
                  </div>
                  {note.moderation.ai_review_notes && (
                    <div className="col-md-6">
                      <strong>AI Notes:</strong>
                      <p className="mb-2">{note.moderation.ai_review_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Flags Section */}
          {note.flags && note.flags.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">User Reports ({note.flags.length})</h5>
              </div>
              <div className="card-body">
                {note.flags.map((flag) => (
                  <div key={flag._id} className="border-bottom pb-3 mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="d-flex gap-2 align-items-center mb-2">
                          <strong>{flag.reason}</strong>
                          <span className={`badge ${flag.status === 'resolved' ? 'bg-success' : 'bg-warning text-dark'}`}>
                            {flag.status}
                          </span>
                        </div>
                        <p className="mb-2">{flag.description}</p>
                        <small className="text-muted">
                          Reported by {flag.reportedBy} on {formatDate(flag.createdAt)}
                        </small>
                        {flag.resolution && (
                          <div className="mt-2">
                            <strong>Resolution:</strong> {flag.resolution}
                            <br />
                            <small className="text-muted">
                              Resolved by {flag.resolvedBy} on {flag.resolvedAt ? formatDate(flag.resolvedAt) : 'Unknown'}
                            </small>
                          </div>
                        )}
                      </div>
                      {flag.status === 'pending' && (
                        <div className="btn-group btn-group-sm">
                          <button 
                            className="btn btn-outline-success"
                            onClick={() => resolveFlag(flag._id, 'Valid concern - content updated')}
                            disabled={saving}
                          >
                            Valid
                          </button>
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={() => resolveFlag(flag._id, 'No action needed - content appropriate')}
                            disabled={saving}
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Column */}
        <div className="col-lg-4">
          {/* Contributor Info */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Contributor Information</h5>
            </div>
            <div className="card-body">
              <p><strong>Alias:</strong> {note.contributorInfo.aliasName || 'Anonymous'}</p>
              {note.contributorInfo.email && (
                <p><strong>Email:</strong> {note.contributorInfo.email}</p>
              )}
              <p><strong>Submitted:</strong> {formatDate(note.createdAt)}</p>
              {note.location && (
                <div>
                  <strong>Location:</strong>
                  <p className="mb-0">
                    {[note.location.city, note.location.state, note.location.zipcode]
                      .filter(Boolean)
                      .join(', ') || 'Not specified'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Moderation Actions */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Moderation Actions</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Decision</label>
                <select 
                  className="form-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as 'moderator-approved' | 'rejected')}
                >
                  <option value="moderator-approved">Approve</option>
                  <option value="rejected">Reject</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Comment (Optional)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={moderatorComment}
                  onChange={(e) => setModeratorComment(e.target.value)}
                  placeholder="Add a comment about your decision..."
                />
              </div>
              
              <button 
                className={`btn w-100 ${selectedStatus === 'rejected' ? 'btn-danger' : 'btn-primary'}`}
                onClick={updateModerationStatus}
                disabled={saving}
              >
                {saving ? 'Processing...' : `${selectedStatus === 'moderator-approved' ? 'Approve' : 'Reject'} Note`}
              </button>
              
              {note.moderation?.moderatorComment && (
                <div className="mt-3">
                  <strong>Previous Comment:</strong>
                  <p className="mb-0 small text-muted">{note.moderation.moderatorComment}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteModerationView; 